import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { MultiSelectCombobox } from '@/components/ui/multi-select-combobox';
import { BillingNav } from './BillingNav';
import { UNIT_LABEL, billingApi, euro, PriceListUnit } from '@/services/billingApi';
import { apiClient } from '@/utils/api';
import { toast } from 'sonner';

type Item = {
  id: string;
  description: string;
  unit: PriceListUnit;
  unitPrice: number;
  isActive: boolean;
  practiceTypeIds: string[];
  practiceTypes?: Array<{ id: string; name: string }>;
};

const emptyForm = {
  id: '',
  description: '',
  unit: 'CORPO' as PriceListUnit,
  unitPrice: 0,
  isActive: true,
  practiceTypeIds: [] as string[],
};

export default function BillingPriceListPage() {
  const [items, setItems] = useState<Item[]>([]);
  const [types, setTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyForm);

  const load = (practiceTypeId?: string) => {
    billingApi
      .priceList(practiceTypeId || undefined)
      .then(setItems)
      .catch((e) => toast.error(e.response?.data?.message || 'Errore'));
  };

  useEffect(() => {
    load(filterType);
    apiClient
      .get('/practice-types')
      .then((r) => {
        const list = r.data.data?.practiceTypes || r.data.data || [];
        setTypes(list.map((t: any) => ({ id: t.id, name: t.name })));
      })
      .catch(() => undefined);
  }, [filterType]);

  const typeOptions = types.map((t) => ({ value: t.id, label: t.name }));
  const typeName = (id: string) => types.find((t) => t.id === id)?.name || id;

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) =>
        item.description.toLowerCase().includes(q) ||
        (item.practiceTypes || []).some((t) => t.name.toLowerCase().includes(q))
    );
  }, [items, search]);

  const save = async () => {
    if (!form.description.trim()) {
      toast.error('Inserisci una descrizione');
      return;
    }
    try {
      await billingApi.savePriceItem({
        id: form.id || undefined,
        description: form.description,
        unit: form.unit,
        unitPrice: form.unitPrice,
        isActive: form.isActive,
        practiceTypeIds: form.practiceTypeIds,
      });
      setForm(emptyForm);
      load(filterType);
      toast.success(form.id ? 'Voce aggiornata' : 'Voce aggiunta');
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Errore salvataggio');
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Listino</h1>
      <BillingNav />
      <Card className="p-4 space-y-3">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Filtro tipo pratica</Label>
            <Combobox
              options={[{ value: '', label: 'Tutti i tipi' }, ...typeOptions]}
              value={filterType}
              onValueChange={setFilterType}
              placeholder="Tutti i tipi"
              searchPlaceholder="Cerca tipo pratica..."
            />
          </div>
          <div>
            <Label>Cerca nel listino</Label>
            <Input placeholder="Descrizione o tipo" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </Card>
      <Card className="p-4 space-y-3">
        <div className="font-medium text-sm">{form.id ? 'Modifica voce' : 'Nuova voce'}</div>
        <div className="grid md:grid-cols-4 gap-2">
          <div className="md:col-span-2">
            <Label>Descrizione</Label>
            <Input
              placeholder="Es. Direzione lavori"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div>
            <Label>Unità</Label>
            <select
              className="w-full border rounded-md h-10 px-2"
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value as PriceListUnit })}
            >
              {Object.entries(UNIT_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Prezzo €</Label>
            <Input
              type="number"
              step="0.01"
              value={form.unitPrice}
              onChange={(e) => setForm({ ...form, unitPrice: Number(e.target.value) })}
            />
          </div>
        </div>
        <div>
          <Label>Tipi pratica</Label>
          <p className="text-xs text-gray-500 mb-1">
            Su un documento con pratica SCIA compariranno queste voci (più quelle senza tipo).
          </p>
          <MultiSelectCombobox
            options={typeOptions}
            value={form.practiceTypeIds}
            onValueChange={(practiceTypeIds) => setForm({ ...form, practiceTypeIds })}
            placeholder="Nessun tipo (voce globale)"
            searchPlaceholder="Cerca tipo pratica..."
            emptyMessage="Nessun tipo pratica"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={save}>{form.id ? 'Aggiorna' : 'Aggiungi'}</Button>
          {form.id && (
            <Button variant="outline" onClick={() => setForm(emptyForm)}>
              Annulla
            </Button>
          )}
        </div>
      </Card>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="p-3">Descrizione</th>
              <th className="p-3">Unità</th>
              <th className="p-3">Prezzo</th>
              <th className="p-3">Tipi</th>
              <th className="p-3">Attiva</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 && (
              <tr>
                <td className="p-6 text-gray-500" colSpan={6}>
                  Nessuna voce
                </td>
              </tr>
            )}
            {filteredItems.map((item) => (
              <tr key={item.id} className="border-b">
                <td className="p-3">{item.description}</td>
                <td className="p-3">{UNIT_LABEL[item.unit]}</td>
                <td className="p-3">{euro(item.unitPrice)}</td>
                <td className="p-3 text-gray-600">
                  {(item.practiceTypes?.length
                    ? item.practiceTypes.map((t) => t.name)
                    : (item.practiceTypeIds || []).map(typeName)
                  ).join(', ') || '—'}
                </td>
                <td className="p-3">{item.isActive ? 'sì' : 'no'}</td>
                <td className="p-3 whitespace-nowrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setForm({
                        id: item.id,
                        description: item.description,
                        unit: item.unit,
                        unitPrice: item.unitPrice,
                        isActive: item.isActive,
                        practiceTypeIds: item.practiceTypeIds || [],
                      })
                    }
                  >
                    Modifica
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      await billingApi.savePriceItem({ ...item, isActive: !item.isActive });
                      load(filterType);
                    }}
                  >
                    {item.isActive ? 'Disattiva' : 'Riattiva'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
